import { Input, Button, Box, useMediaQuery, IconButton } from "@chakra-ui/react";
import { SearchIcon } from "./SearchIcon";
import React, { FC, useState } from "react";
import { useTheme } from "@emotion/react";
import { LuX } from 'react-icons/lu';

export type SearchBoxProps = {
  triggerSearch(value: string): void;
  searchValue: string;
  placeholder?: string;
};

/******************
 * SearchBox component
 *
 * Can be reused on every form/page where search is needed
 * Props:
 *  triggerSearch(value) -> this function is called whe the user entered a value
 *  searchValue -> keep the searchvalue between different renders or initialize it
 *  size? : lg md sm xs
 *  width?: number of pixels the search box can be wide
 *  placeholder: placeholder to show
 */
export const SearchBox: FC<SearchBoxProps> = ({
  triggerSearch,
  searchValue,
  placeholder,
}) => {
  const [expanded, setExpanded] = useState(Boolean(searchValue));
  const [searchVal, setSearchVal] = useState(searchValue);
  const [isSmallMediaScreen] = useMediaQuery(["(max-width: 1100px)"]);
  const [isVerySmallMediaScreen] = useMediaQuery(["(max-width: 547px)"]);
  const theme = useTheme();

  const buttonClicked = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (expanded || searchVal) {
      triggerSearch(searchVal);
      if (!searchVal) {
        setExpanded(false);
      }
    } else {
      setExpanded(true);
    }
  };

  const handleSearchValChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchVal(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      triggerSearch(searchVal);
    }
  };

  const handleClearInput = () => {
    setSearchVal("");
    triggerSearch("");
    setExpanded(false);
  };

  const showInput = expanded || isSmallMediaScreen;

  return (
    <Box height="32px" width={isVerySmallMediaScreen ? "100%" : "200px"}>
      <Box
        width={isSmallMediaScreen ? "100%" : "200px"}
        mt={isSmallMediaScreen ? "2" : "0"}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-end"
      >
        {showInput && (
          <Input
            flex="1"
            minWidth={0}
            borderRadius="2xl"
            placeholder={placeholder}
            css={{
              '--focus-color': theme.colors.text.secondary
            }}
            autoFocus
            value={searchVal}
            onChange={handleSearchValChange}
            onKeyDown={handleKeyDown}
            height="8"
            mr="1%"
          />
        )}
        <Box
          display="flex"
          flexDirection="row"
          flexShrink={0}
          alignItems="center"
          height="32px"
          width={searchVal ? "68px" : "48px"}
        >
          {searchVal && (
            <IconButton
              display="flex"
              aria-label="Clear search"
              size="sm"
              onClick={handleClearInput}
              variant="ghost"
              minWidth="16px"
              h="16px"
              mr="4px"
              opacity="0.4"
              fontWeight="bold"><LuX /></IconButton>
          )}
          <Button
            h="16px"
            borderRadius="lg"
            onClick={buttonClicked}
            value={searchVal}
            padding="0"
            minWidth="16px"
            display="flex"
            variant="ghost"
          >
            <SearchIcon />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
